import React, { useState, useEffect } from 'react';
import OptimizedImage from '../common/OptimizedImage';

// Capitalize first letter of each word (for free-text fields like insurance names)
const capitalizeWords = (str) => {
  if (!str) return str;
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

function ProfileContent({ therapist }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showAllPhotos, setShowAllPhotos] = useState(false);

  // Hide body scroll when lightbox is open
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on component unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedPhoto]);

  // Photo Gallery Component - Only show with real content
  const PhotoGallery = () => {
    // Only use real photos/videos - no demo content
    const hasRealPhotos = therapist.additionalPhotos && therapist.additionalPhotos.length > 0;
    const hasVideo = therapist.profileVideo;

    // Don't show gallery if no real content
    if (!hasRealPhotos && !hasVideo) return null;

    const photoCount = hasRealPhotos ? therapist.additionalPhotos.length : 0;
    const photosToShow = showAllPhotos ? therapist.additionalPhotos : therapist.additionalPhotos?.slice(0, 2) || [];
    const totalItems = (hasVideo ? 1 : 0) + photosToShow.length;
    const hasMorePhotos = photoCount > 2 && !showAllPhotos;

    // Determine grid configuration based on total items
    const getGridConfig = () => {
      if (totalItems <= 3) {
        return 'grid-cols-2 md:grid-cols-3'; // 2 cols mobile, 3 cols desktop
      } else if (totalItems <= 6) {
        return 'grid-cols-2 md:grid-cols-4'; // 2 cols mobile, 4 cols desktop  
      } else {
        return 'grid-cols-2 md:grid-cols-5'; // 2 cols mobile, 5 cols desktop
      }
    };

    const openLightbox = (photoIndex) => {
      setCurrentPhotoIndex(photoIndex);
      setSelectedPhoto(therapist.additionalPhotos[photoIndex]);
    };

    const closeLightbox = () => {
      setSelectedPhoto(null);
    };

    const nextPhoto = () => {
      const nextIndex = (currentPhotoIndex + 1) % therapist.additionalPhotos.length;
      setCurrentPhotoIndex(nextIndex);
      setSelectedPhoto(therapist.additionalPhotos[nextIndex]);
    };

    const prevPhoto = () => {
      const prevIndex = (currentPhotoIndex - 1 + therapist.additionalPhotos.length) % therapist.additionalPhotos.length;
      setCurrentPhotoIndex(prevIndex);
      setSelectedPhoto(therapist.additionalPhotos[prevIndex]);
    };

    return (
      <>
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#254e70]/10">
          <h2 className="text-xl font-bold text-[#254e70] mb-6" style={{ fontFamily: 'Kanit, sans-serif' }}>
            {hasVideo && hasRealPhotos ? 'Introduction & Photos' : 
             hasVideo ? 'Introduction Video' : 'Office & Practice Photos'}
          </h2>
          
          {/* Masonry Grid Layout */}
          <div className={`grid ${getGridConfig()} gap-4 auto-rows-[200px]`}>
            
            {/* Video - Always takes 2x2 space when present */}
            {hasVideo && (
              <div className="col-span-2 row-span-2 group">
                <div className="relative h-full rounded-xl overflow-hidden bg-gray-50 shadow-md">
                  <video 
                    src={therapist.profileVideo}
                    controls 
                    poster={therapist.profilePhoto}
                    className="w-full h-full object-cover"
                    preload="metadata"
                  >
                    Your browser does not support the video tag.
                  </video>
                  
                  {/* Video label */}
                  <div className="absolute top-3 left-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                    <svg className="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                    Meet {therapist.firstName}
                  </div>

                  {/* Play button overlay for better UX */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <div className="bg-white/90 text-[#254e70] rounded-full p-4 shadow-lg">
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Photos with dynamic sizing */}
            {hasRealPhotos && photosToShow.map((photo, index) => {
              const photoUrl = photo.url || photo;
              const photoCaption = photo.caption || photo.alt || '';
              
              return (
                <div key={index} className="group relative cursor-pointer" onClick={() => openLightbox(index)}>
                  <div className="relative h-full rounded-lg overflow-hidden bg-gray-50 shadow-sm hover:shadow-md transition-all duration-200">
                    <OptimizedImage
                      src={photoUrl}
                      alt={photoCaption || `Practice photo ${index + 1}`}
                      className="w-full h-full group-hover:scale-105 transition-transform duration-200"
                      loading="lazy"
                      sizes="(max-width: 768px) 50vw, 25vw"
                      fallbackIcon={
                        <div className="w-full h-full bg-red-50 border border-red-200 flex items-center justify-center text-red-500 text-sm">
                          ❌ Image unavailable
                        </div>
                      }
                    />
                    
                    {/* Photo caption overlay */}
                    {photoCaption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <p className="text-white text-sm font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                          {photoCaption}
                        </p>
                      </div>
                    )}

                    {/* Expand icon */}
                    <div className="absolute top-2 right-2 bg-white/80 text-[#254e70] p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              );
            })}

          </div>

          {/* Gallery summary */}
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-600" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
              {hasVideo && hasRealPhotos && (
                <>Introduction video + {photosToShow.length} photo{photosToShow.length !== 1 ? 's' : ''}</>
              )}
              {hasVideo && !hasRealPhotos && (
                <>Introduction video</>
              )}
              {!hasVideo && hasRealPhotos && (
                <>{photosToShow.length} photo{photosToShow.length !== 1 ? 's' : ''}</>
              )}
            </span>
            
            <div className="flex items-center gap-3">
              {/* Show More Button */}
              {hasMorePhotos && (
                <button 
                  onClick={() => setShowAllPhotos(true)}
                  className="inline-flex items-center gap-1 px-3 py-1 text-xs text-[#254e70] bg-white border border-[#254e70]/20 rounded-lg hover:bg-[#254e70]/5 hover:border-[#254e70]/40 transition-all duration-200"
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  view more ({photoCount - 2})
                </button>
              )}
              
              {/* Show Less Button */}
              {showAllPhotos && photoCount > 2 && (
                <button 
                  onClick={() => setShowAllPhotos(false)}
                  className="text-xs text-[#9dacff] hover:text-[#254e70] transition-colors duration-200" 
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  Show less
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Lightbox Modal */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4 overflow-hidden" 
            onClick={closeLightbox}
            style={{ margin: 0, padding: '1rem' }}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Close button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 bg-white/80 hover:bg-white text-[#254e70] p-2 rounded-full shadow-lg transition-colors duration-200"
                style={{ zIndex: 10001 }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Main image container */}
              <div className="relative w-full h-full flex items-center justify-center">
                <OptimizedImage
                  src={selectedPhoto.url || selectedPhoto}
                  alt={selectedPhoto.caption || selectedPhoto.alt || 'Practice photo'}
                  className="max-w-full max-h-full rounded-lg shadow-2xl"
                  loading="eager"
                  sizes="100vw"
                  objectFit="contain"
                  style={{ 
                    maxWidth: 'calc(100vw - 2rem)', 
                    maxHeight: 'calc(100vh - 2rem)',
                    width: 'auto',
                    height: 'auto'
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Navigation arrows */}
              {therapist.additionalPhotos.length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-[#254e70] p-3 rounded-full shadow-lg transition-colors duration-200"
                    style={{ zIndex: 10001 }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <button
                    onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-[#254e70] p-3 rounded-full shadow-lg transition-colors duration-200"
                    style={{ zIndex: 10001 }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Photo info */}
              <div className="absolute bottom-4 left-4 right-4 bg-black/60 text-white p-4 rounded-lg" style={{ zIndex: 10001 }}>
                <div className="flex justify-between items-center">
                  <div>
                    {(selectedPhoto.caption || selectedPhoto.alt) && (
                      <p className="text-lg font-medium mb-1" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        {selectedPhoto.caption || selectedPhoto.alt}
                      </p>
                    )}
                    <p className="text-sm opacity-80" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                      Photo {currentPhotoIndex + 1} of {therapist.additionalPhotos.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Therapist Quote Component
  const TherapistQuote = () => {
    if (!therapist.quote) return null;

    return (
      <div className="bg-gradient-to-br from-[#9dacff]/10 to-[#254e70]/5 rounded-2xl p-8 shadow-lg border border-[#9dacff]/20">
        <div className="flex items-start gap-4">
          <svg className="w-8 h-8 text-[#9dacff] flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
          </svg>
          <div className="flex-1">
            <blockquote className="text-lg italic text-[#254e70] leading-relaxed mb-4">
              "{therapist.quote}"
            </blockquote>
            <cite className="text-sm font-medium text-[#254e70]/70 not-italic">
              — {therapist.firstName} {therapist.lastName}
              {therapist.pronouns && ` (${therapist.pronouns})`}
            </cite>
          </div>
        </div>
      </div>
    );
  };

  // Professional Credentials Component
  const ProfessionalCredentials = () => {
    const hasCredentials = therapist.credentials?.length > 0 || 
                          therapist.professional?.licenses?.length > 0 ||
                          therapist.professional?.certifications?.length > 0 ||
                          therapist.professional?.yearsExperience ||
                          therapist.professional?.education?.length > 0;

    if (!hasCredentials) return null;

    return (
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#254e70]/10">
        <h2 className="text-2xl font-bold text-[#254e70] mb-4">Professional Credentials</h2>
        
        <div className="space-y-4">
          {/* Credentials Row */}
          {therapist.credentials?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#254e70] mb-2">Credentials</h3>
              <div className="flex flex-wrap gap-2">
                {therapist.credentials.map((credential, index) => (
                  <span key={index} className="px-2 py-1 bg-[#9dacff]/10 text-[#254e70] rounded text-xs font-medium">
                    {typeof credential === 'object' ? credential.name : credential}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Experience & Languages Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {therapist.professional?.yearsExperience && (
              <div>
                <h3 className="text-sm font-semibold text-[#254e70] mb-1">Experience</h3>
                <p className="text-sm text-[#254e70]/80">{therapist.professional.yearsExperience}</p>
              </div>
            )}

            {therapist.professional?.languages?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#254e70] mb-1">Languages</h3>
                <div className="flex flex-wrap gap-1">
                  {therapist.professional.languages.map((language, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-[#254e70] rounded text-xs">
                      {typeof language === 'object' ? language.name : language}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Licenses */}
          {therapist.professional?.licenses?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#254e70] mb-2">Licenses</h3>
              <div className="space-y-2">
                {therapist.professional.licenses.map((license, index) => (
                  <div key={index} className="text-sm text-[#254e70]/80">
                    <span className="font-medium">{typeof license === 'object' ? license.licenseType || license.type || license.name : license}</span>
                    {license.number && <span className="ml-2 text-[#254e70]/60">#{license.number}</span>}
                    {license.state && <span className="ml-2 text-[#254e70]/60">({license.state})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {therapist.professional?.education?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#254e70] mb-2">Education</h3>
              <div className="space-y-2">
                {therapist.professional.education.map((edu, index) => (
                  <div key={index} className="text-sm text-[#254e70]/80">
                    <span className="font-medium">{typeof edu === 'object' ? edu.degree || edu.name : edu}</span>
                    {edu.school && <span className="ml-2">- {edu.school}</span>}
                    {edu.year && <span className="ml-2 text-[#254e70]/60">({edu.year})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Certifications */}
          {therapist.professional?.certifications?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#254e70] mb-2">Certifications</h3>
              <div className="flex flex-wrap gap-2">
                {therapist.professional.certifications.map((cert, index) => (
                  <span key={index} className="px-2 py-1 bg-[#254e70]/10 text-[#254e70] rounded text-xs font-medium">
                    {typeof cert === 'object' ? cert.name : cert}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Session Types Component
  const SessionTypes = () => {
    if (!therapist.sessionTypes?.length && !therapist.rates) return null;

    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
        <h2 className="text-2xl font-bold text-[#254e70] mb-6">Sessions & Rates</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Session Types */}
          {therapist.sessionTypes?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-[#254e70] mb-3">Session Types</h3>
              <div className="space-y-2">
                {therapist.sessionTypes.map((type, index) => (
                  <div key={index} className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span className="text-sm">{typeof type === 'object' ? type.name : type}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rates */}
          {therapist.rates && (
            <div>
              <h3 className="text-lg font-semibold text-[#254e70] mb-3">Rates</h3>
              <div className="space-y-3">
                {therapist.rates.individual && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#254e70]/80">Individual Session</span>
                    <span className="font-medium text-[#254e70]">${therapist.rates.individual}</span>
                  </div>
                )}
                {therapist.rates.couples && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#254e70]/80">Couples Session</span>
                    <span className="font-medium text-[#254e70]">${therapist.rates.couples}</span>
                  </div>
                )}
                {therapist.rates.family && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#254e70]/80">Family Session</span>
                    <span className="font-medium text-[#254e70]">${therapist.rates.family}</span>
                  </div>
                )}
                {therapist.rates.group && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-[#254e70]/80">Group Session</span>
                    <span className="font-medium text-[#254e70]">${therapist.rates.group}</span>
                  </div>
                )}
              </div>

              {/* Sliding Scale */}
              {therapist.rates.slidingScale && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200 mt-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h4 className="font-semibold text-green-800">Sliding Scale Available</h4>
                  </div>
                  <p className="text-sm text-green-700 mt-1">Reduced rates available based on financial need</p>
                </div>
              )}

              {/* Free Consultation */}
              {therapist.rates.freeConsultation?.offered && (
                <div className="bg-[#9dacff]/10 rounded-lg p-3 border border-[#9dacff]/20 mt-4">
                  <h4 className="font-semibold text-[#254e70] mb-1">Free Consultation</h4>
                  {therapist.rates.freeConsultation.duration && (
                    <p className="text-sm text-[#254e70]/80">
                      {therapist.rates.freeConsultation.duration} minutes
                    </p>
                  )}
                  {therapist.rates.freeConsultation.description && (
                    <p className="text-sm text-[#254e70]/70 mt-1">
                      {therapist.rates.freeConsultation.description}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Insurance & Payment Component
  const InsuranceAndPayment = () => {
    const hasInsurance = therapist.insuranceAccepted?.length > 0 || therapist.insurance;
    const hasPayment = therapist.paymentMethods;

    if (!hasInsurance && !hasPayment) return null;

    return (
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
        <h2 className="text-2xl font-bold text-[#254e70] mb-6">Insurance & Payment</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Insurance */}
          {hasInsurance && (
            <div>
              <h3 className="text-lg font-semibold text-[#254e70] mb-3">Insurance</h3>
              
              {therapist.insuranceAccepted?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-[#254e70] mb-2">Accepted Insurance</h4>
                  <div className="flex flex-wrap gap-2">
                    {therapist.insuranceAccepted.map((insurance, index) => (
                      <span key={index} className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm">
                        {capitalizeWords(typeof insurance === 'object' ? insurance.name : insurance)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {therapist.insurance && (
                <div className="space-y-2 text-sm">
                  {therapist.insurance.outOfNetwork && (
                    <div className="flex items-center gap-2 text-[#254e70]">
                      <span className="text-green-500">✓</span>
                      <span>Out-of-network benefits accepted</span>
                    </div>
                  )}
                  {therapist.insurance.providesSuperbill && (
                    <div className="flex items-center gap-2 text-[#254e70]">
                      <span className="text-green-500">✓</span>
                      <span>Provides superbills for reimbursement</span>
                    </div>
                  )}
                  {therapist.insurance.priorAuthRequired && (
                    <div className="flex items-center gap-2 text-[#254e70]/70">
                      <span className="text-yellow-500">!</span>
                      <span>Prior authorization may be required</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Payment Methods */}
          {hasPayment && (
            <div>
              <h3 className="text-lg font-semibold text-[#254e70] mb-3">Payment Methods</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {therapist.paymentMethods.acceptsCash && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>Cash</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsCheck && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>Check</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsVisa && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>Visa</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsMastercard && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>Mastercard</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsAmericanExpress && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>Amex</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsPayPal && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>PayPal</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsVenmo && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>Venmo</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsHSA && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>HSA</span>
                  </div>
                )}
                {therapist.paymentMethods.acceptsFSA && (
                  <div className="flex items-center gap-2 text-[#254e70]">
                    <span className="text-green-500">✓</span>
                    <span>FSA</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Explore More in City Component
  const ExploreMoreInCity = () => {
    // Only show if therapist has location data
    if (!therapist.location?.city || !therapist.location?.state) return null;

    const cityName = capitalizeWords(therapist.location.city);
    const stateName = therapist.location.state;

    // Format for URL (lowercase, spaces to hyphens)
    const citySlug = cityName.toLowerCase().replace(/\s+/g, '-');
    const stateSlug = stateName.toLowerCase();
    
    const cityPageUrl = `/therapists/${stateSlug}/${citySlug}`;

    return (
      <div className="bg-blue-50 rounded-2xl p-8">
        <div className="text-center">
          <div className="mb-4">
            <svg className="w-12 h-12 text-[#254e70] mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit, sans-serif' }}>
            Looking for mental health professionals in {cityName}?
          </h3>
          <p className="text-[#254e70]/70 mb-8 text-lg" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
            Explore more licensed therapists and mental health professionals in your area.
          </p>
          <a
            href={cityPageUrl}
            className="inline-flex items-center gap-3 bg-[#254e70] hover:bg-[#1d3e5a] text-white font-bold px-8 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            style={{ fontFamily: 'Nunito Sans, sans-serif' }}
          >
            <span>Explore {cityName} Therapists</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Bio Section */}
      {therapist.bio && (
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
          <h2 className="text-2xl font-bold text-[#254e70] mb-4" style={{ fontFamily: 'Kanit, sans-serif' }}>About {therapist.firstName}</h2>
          <p className="text-[#254e70]/80 leading-relaxed whitespace-pre-line" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
            {therapist.bio}
          </p>
        </div>
      )}

      {/* Media Gallery (Video + Photos) - Only with real content */}
      <PhotoGallery />

      {/* Therapist Quote */}
      <TherapistQuote />

      {/* Professional Credentials */}
      <ProfessionalCredentials />

      {/* Specializations & Treatment Approaches */}
      {(therapist.specializations?.length > 0 || therapist.treatmentApproaches?.length > 0) && (
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#254e70]/10">
          <h2 className="text-2xl font-bold text-[#254e70] mb-6">Specializations & Treatment Approaches</h2>
          
          <div className="space-y-6">
            {therapist.specializations?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-[#254e70] mb-3">Areas of Specialization</h3>
                <div className="flex flex-wrap gap-3">
                  {therapist.specializations.map((specialty, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#9dacff]/10 text-[#254e70] rounded-lg border border-[#9dacff]/20 font-medium"
                    >
                      {typeof specialty === 'object' ? specialty.name : specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {therapist.treatmentApproaches?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-[#254e70] mb-3">Treatment Approaches</h3>
                <div className="flex flex-wrap gap-3">
                  {therapist.treatmentApproaches.map((approach, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-[#254e70]/10 text-[#254e70] rounded-lg font-medium"
                    >
                      {typeof approach === 'object' ? approach.name : approach}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Session Types & Rates */}
      <SessionTypes />

      {/* Insurance & Payment */}
      <InsuranceAndPayment />

      {/* Explore More in City */}
      <ExploreMoreInCity />
    </div>
  );
}

export default ProfileContent; 